import styled, { useTheme } from 'styled-components';

import { FinancialAggregate } from '../../../utils/financesUtils';
import penceToPound from '../../../utils/penceToPound';

const Block = styled.div`
  margin-bottom: 30px;
`;

const BlockTitle = styled.h3`
  font-size: 16px;
  font-weight: bold;
  color: ${(props) => props.theme.colors.dark};
  margin-bottom: 4px;
`;

const InsetPanel = styled.div`
  border: 1px solid ${(props) => props.theme.colors.light};
  border-radius: 4px;
  padding: 16px;
  background: ${(props) => props.theme.colors.white};
`;

const Line = styled.p`
  font-size: 16px;
  line-height: 26px;
  margin-bottom: 0;
`;

const Highlight = styled.span<{ $color: string }>`
  color: ${(props) => props.$color};
  font-weight: bold;
`;

const TotalLine = styled.p`
  font-size: 16px;
  font-weight: bold;
  margin-top: 16px;
  margin-bottom: 0;
`;

const Callout = styled.div`
  margin-top: 16px;
  background-color: ${(props) => props.theme.colors.pale ?? '#e8edf8'};
  border-radius: 4px;
  padding: 8px;
  font-size: 16px;
  line-height: 26px;
`;

const CalloutText = styled.p`
  margin-bottom: 4px;
`;

const CalloutBold = styled.p`
  font-weight: bold;
  margin-bottom: 0;
`;

interface MonthSummaryBlockProps {
  title: string;
  monthName: string;
  isCurrentMonth: boolean;
  aggregate: FinancialAggregate;
}

const MonthSummaryBlock = ({ title, monthName, isCurrentMonth, aggregate }: MonthSummaryBlockProps) => {
  const theme = useTheme();
  const { exportEarnings, importCosts, optimiseSaving } = aggregate;

  const netEarnings = exportEarnings - importCosts;
  const baselineEarnings = netEarnings - optimiseSaving;
  const prefix = isCurrentMonth ? "You've" : 'You';

  const netIsPositive = netEarnings >= 0;
  const netLabel = netIsPositive ? 'earnings' : 'cost';
  const baselineIsPositive = baselineEarnings >= 0;
  const baselineLabel = baselineIsPositive ? 'earnings' : 'cost';
  const timeClause = isCurrentMonth ? 'so far this month' : `in ${monthName}`;

  return (
    <Block>
      <BlockTitle>{title}</BlockTitle>
      <InsetPanel>
        <Line>
          {prefix} <Highlight $color={theme.colors.imported}>imported</Highlight> £{penceToPound(importCosts, true)}{' '}
          energy
        </Line>
        <Line>
          {prefix} <Highlight $color={theme.colors.exported}>exported</Highlight> £{penceToPound(exportEarnings, true)}{' '}
          energy
        </Line>
        <TotalLine>
          So your net energy {netLabel} {timeClause} is £{penceToPound(Math.abs(netEarnings), true)}
        </TotalLine>
        <Callout>
          <CalloutText>
            Your net energy {baselineLabel} would have been £{penceToPound(Math.abs(baselineEarnings), false)} without
            any optimisation.
          </CalloutText>
          <CalloutBold>
            So by using Loop Optimise you have saved an extra £{penceToPound(Math.abs(optimiseSaving), false)}
          </CalloutBold>
        </Callout>
      </InsetPanel>
    </Block>
  );
};

interface MonthSummaryProps {
  currentMonthLabel: string;
  lastMonthLabel: string;
  currentMonth: FinancialAggregate | null;
  lastMonth: FinancialAggregate | null;
}

const MonthSummary = ({ currentMonthLabel, lastMonthLabel, currentMonth, lastMonth }: MonthSummaryProps) => {
  if (!currentMonth && !lastMonth) return null;

  return (
    <div className="mb-4">
      {currentMonth && (
        <MonthSummaryBlock
          title={`This month so far (${currentMonthLabel})`}
          monthName={currentMonthLabel}
          isCurrentMonth
          aggregate={currentMonth}
        />
      )}
      {lastMonth && (
        <MonthSummaryBlock
          title={`Last month (${lastMonthLabel})`}
          monthName={lastMonthLabel}
          isCurrentMonth={false}
          aggregate={lastMonth}
        />
      )}
    </div>
  );
};

export default MonthSummary;
